import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

/* ------------------------------------------------------------------
   ویرایشگر متن نامه — «ورد، ولی برای نامه‌نگاری»
   ------------------------------------------------------------------
   🔴 چرا از صفر و بدون کتابخانه.

   ویرایشگرهای آماده (TipTap، Quill، Slate) همگی LTR-first هستند و
   راست‌به‌چپ در آن‌ها یک «حالت» است، نه پیش‌فرض. نتیجه‌اش در فارسی
   همیشه یکی است: نشانگر در جای غلط، تراز پاراگراف قاطی، فهرست‌های
   شماره‌دار با ارقام لاتین، و چاپی که حاشیه‌هایش برعکس است.

   ⚠️ و مهم‌تر: نامهٔ اداری «سند» است، نه محتوای وب. باید روی کاغذ A4
   دقیقاً همان‌طور دربیاید که روی صفحه دیده می‌شود — با همان حاشیه،
   همان فاصلهٔ سرلوحه، همان جای امضا. هیچ‌کدام از آن کتابخانه‌ها این
   را نمی‌دهند؛ باید خودش نوشته شود.

   ------------------------------------------------------------------
   ⚠️ `document.execCommand` منسوخ اعلام شده ولی جایگزین ندارد.

   تنها جایگزین واقعی، پیاده‌سازی کامل یک مدل سند و مدیریت Selection
   است — یعنی همان کتابخانه‌های چندهزار خطی. همهٔ مرورگرهای امروز
   `execCommand` را پشتیبانی می‌کنند و حذفش برنامه‌ریزی نشده. این یک
   انتخاب آگاهانه است، نه غفلت.
   ------------------------------------------------------------------ */

export type RichTextEditorProps = {
  value: string
  onChange: (html: string) => void
  /** جهت پیش‌فرض — نامهٔ فارسی همیشه rtl */
  dir?: 'rtl' | 'ltr'
  placeholder?: string
  disabled?: boolean
  /** نمایش خط‌کش بالای صفحه */
  showRuler?: boolean
  /** ناحیهٔ سرلوحه — لوگو و مشخصات سازمان در چاپ */
  letterhead?: ReactNode
  /** ناحیهٔ پاصفحه — امضا و مهر */
  footer?: ReactNode
  className?: string
  minHeightMm?: number
}

export type RichTextEditorHandle = {
  focus: () => void
  print: () => void
  getHtml: () => string
}

/* ------------------------------------------------------------------
   اندازهٔ کاغذ — میلی‌متر، چون سند کاغذی است نه صفحهٔ وب.
   ------------------------------------------------------------------ */
const A4_WIDTH_MM = 210
const DEFAULT_MARGIN_MM = { top: 25, right: 25, bottom: 25, left: 25 }

const FONTS = [
  { label: 'ایران‌سنس', value: 'IRANSans, Vazirmatn, Tahoma, sans-serif' },
  { label: 'وزیر', value: 'Vazirmatn, IRANSans, Tahoma, sans-serif' },
  { label: 'نازنین', value: '"B Nazanin", Nazanin, Times, serif' },
  { label: 'میترا', value: '"B Mitra", Mitra, Times, serif' },
  { label: 'تیتر', value: '"B Titr", Titr, Tahoma, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Arial, sans-serif' },
  { label: 'Times', value: '"Times New Roman", Times, serif' },
]

/* اندازه‌های ورد، نه اندازه‌های وب. کاربر «۱۲» می‌شناسد نه «14px». */
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 36, 48, 72]

const COLORS = [
  '#000000', '#1f2937', '#374151', '#6b7280',
  '#b91c1c', '#dc2626', '#ea580c', '#ca8a04',
  '#15803d', '#059669', '#0891b2', '#1d4ed8',
  '#4338ca', '#7e22ce', '#be185d', '#ffffff',
]

const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fecaca', '#e9d5ff', 'transparent']

/* ------------------------------------------------------------------
   پاک‌سازی HTML چسبانده‌شده.

   🔴 کاربر متن را از ورد کپی می‌کند و ورد صدها خط CSS و تگ
   `<o:p>` و `<w:WordDocument>` با خودش می‌آورد. بدون پاک‌سازی:

     · حجم نامه ده‌ها برابر می‌شود
     · استایل‌های ورد بر استایل سند غالب می‌شوند و چاپ خراب می‌شود
     · و مهم‌تر — `<script>` و `onerror` هم از همین راه وارد می‌شوند

   ⚠️ این پاک‌سازیِ سمت کلاینت **جایگزین** پاک‌سازی سمت سرور نیست.
   هر کسی می‌تواند مستقیم به API بزند. سرور هم همین کار را می‌کند؛
   این یکی فقط تجربهٔ کاربر را درست می‌کند.
   ------------------------------------------------------------------ */
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'DIV', 'SPAN', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'UL', 'OL', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TD', 'TH', 'BLOCKQUOTE', 'HR', 'SUB', 'SUP', 'A',
])

const ALLOWED_ATTRS = new Set(['style', 'colspan', 'rowspan', 'dir', 'align', 'href', 'target'])

/*
 | 🔴 این تگ‌ها با **محتوایشان** حذف می‌شوند — همان فهرستِ سرور
 | (`LetterWordDocument::DROPPED_WITH_CONTENT`).
 |
 | ⚠️ قاعدهٔ عمومی این است که تگِ ناشناخته باز شود و محتوایش بماند. برای
 | `<style>`ِ Word همان قاعده یعنی تعریفِ سبک‌ها («p.MsoNormal {…}») به
 | شکلِ متنِ خواندنی وسطِ نامه بنشیند.
 */
const DROPPED_WITH_CONTENT = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE',
  'HEAD', 'META', 'LINK', 'TITLE', 'XML',
])

/**
 * فقط ویژگی‌های ظاهریِ بی‌خطر — نه `position`، نه `behavior`، نه `url()`.
 *
 * ⚠️ تورفتگیِ سطرِ اول و فاصلهٔ بالا و پایینِ بند هم مجازند: بدونشان
 * بندهای چسبانده‌شده از Word به هم می‌چسبیدند و تورفتگی‌شان می‌رفت.
 */
const ALLOWED_STYLES = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'font-weight', 'font-style',
  'text-align', 'text-decoration', 'direction', 'margin-right', 'margin-left',
  'margin-top', 'margin-bottom', 'margin', 'text-indent',
  'padding-right', 'padding-left', 'line-height', 'width', 'border', 'border-collapse',
  'vertical-align',
])

/*
 | 🔴 نیم‌فاصلهٔ Word.
 |
 | ⚠️ بسیاری از نامه‌های فارسی در Word نیم‌فاصله را با Ctrl+- می‌نویسند،
 | یعنی «خط تیرهٔ اختیاری» (U+00AD) و نه نیم‌فاصلهٔ استاندارد (U+200C).
 | Word آن را مثل نیم‌فاصله نشان می‌دهد؛ مرورگر نادیده‌اش می‌گیرد و حروف
 | به هم می‌چسبند — «می‌شود» می‌شود «میشود». «¬» (U+00AC) هم در بعضی
 | فایل‌های قدیمی همین نقش را دارد.
 |
 | فقط میان دو حرفِ فارسی جایگزین می‌شود؛ جای دیگر معنای خودش را دارد.
 */
const WORD_HALF_SPACE = /([؀-ۿ])[­¬]+(?=[؀-ۿ])/g

function fixHalfSpaces(text: string): string {
  return text.replace(WORD_HALF_SPACE, '$1‌')
}

/** ویژگی‌هایی که از کلاسِ بندِ Word به خودِ بند منتقل می‌شوند. */
const CLASS_STYLES = new Set([
  'margin', 'margin-top', 'margin-bottom', 'margin-right', 'margin-left',
  'text-indent', 'text-align', 'direction',
])

/**
 * HTMLِ کلیپ‌بورد ⟶ فقط بخشی که کاربر کپی کرده، با فاصله‌های بندِ Word.
 *
 * ==================================================================
 * 🔴 دو کار که پاک‌سازیِ عمومی نمی‌تواند بکند:
 *
 * ۱. مرورگر کلِ سندِ Word را به کلیپ‌بورد می‌دهد — با `<head>` و صدها
 *    خط تعریفِ سبک. کپیِ واقعیِ کاربر میان `<!--StartFragment-->` و
 *    `<!--EndFragment-->` است؛ فقط همان برداشته می‌شود.
 *
 * ۲. فاصلهٔ میانِ بندها و تورفتگی در Word معمولاً روی **کلاسِ** بند
 *    است («p.MsoNormal {margin-bottom: 8pt}») و نه روی خودِ بند. کلاس
 *    در پاک‌سازی حذف می‌شود، پس پیش از آن، همین چند ویژگی از تعریفِ
 *    کلاس به `style`ِ خودِ بند منتقل می‌شوند.
 *
 * ⚠️ ویژگی‌های کلاس **پیش از** `style`ِ خودِ بند نوشته می‌شوند تا
 * همان ترتیبِ CSS بماند: آنچه روی خودِ بند آمده برنده است.
 *
 * ⚠️ قلم و فاصلهٔ خطِ کلاس عمداً منتقل نمی‌شوند: قلمِ Normalِ Word
 * (Calibri) قلمِ نامه را می‌پوشاند، و «107%»ِ Word نسبت به ارتفاعِ قلم
 * است نه اندازه‌اش — در مرورگر سطرها را به هم می‌چسباند.
 * ==================================================================
 */
function prepareClipboardHtml(html: string): string {
  const source = new DOMParser().parseFromString(html, 'text/html')
  const rules = new Map<string, string[]>()

  for (const style of Array.from(source.querySelectorAll('style'))) {
    const css = (style.textContent ?? '').replace(/<!--|-->/g, '').replace(/\/\*[\s\S]*?\*\//g, '')

    for (const [, selectors, body] of css.matchAll(/([^{}@]+)\{([^{}]*)\}/g)) {
      const declarations = body
        .split(';')
        .map((rule) => rule.split(':').map((x) => x.trim()))
        .filter(([prop, value]) => prop && value && CLASS_STYLES.has(prop.toLowerCase()))
        .map(([prop, value]) => `${prop.toLowerCase()}: ${value}`)

      if (declarations.length === 0) continue

      /* ⚠️ Word هر کلاس را سه بار نام می‌برد («p.MsoNormal, li.MsoNormal, div.MsoNormal»). */
      const classes = new Set<string>()

      for (const selector of selectors.split(',')) {
        const match = /^\s*[a-z0-9]*\.([\w-]+)\s*$/i.exec(selector)

        if (match) classes.add(match[1])
      }

      for (const name of classes) rules.set(name, [...(rules.get(name) ?? []), ...declarations])
    }
  }

  const START = '<!--StartFragment-->'
  const start = html.indexOf(START)
  const end = html.indexOf('<!--EndFragment-->')

  const doc =
    start !== -1 && end > start
      ? new DOMParser().parseFromString(html.slice(start + START.length, end), 'text/html')
      : source

  if (rules.size > 0) {
    for (const el of Array.from(doc.body.querySelectorAll('[class]'))) {
      const fromClass = Array.from(el.classList).flatMap((name) => rules.get(name) ?? [])

      if (fromClass.length === 0) continue

      const own = el.getAttribute('style')
      el.setAttribute('style', own ? `${fromClass.join('; ')}; ${own}` : fromClass.join('; '))
    }
  }

  return doc.body.innerHTML
}

function sanitizeStyle(style: string): string {
  return style
    .split(';')
    .map((rule) => rule.trim())
    .filter((rule) => {
      const [prop, val] = rule.split(':').map((x) => x?.trim().toLowerCase() ?? '')
      if (!prop || !val) return false
      if (!ALLOWED_STYLES.has(prop)) return false
      /* `url(...)` و `expression(...)` راه‌های شناخته‌شدهٔ تزریق‌اند. */
      return !val.includes('url(') && !val.includes('expression(') && !val.includes('javascript:')
    })
    .join('; ')
}

export function sanitizeLetterHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return ''

  const walk = (parent: Element) => {
    for (const node of Array.from(parent.childNodes)) {
      /* توضیحاتِ شرطیِ Word (`<!--[if …]>`) — نه متن‌اند و نه قالب. */
      if (node.nodeType === Node.COMMENT_NODE) {
        node.remove()
        continue
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.nodeValue ?? ''
        const fixed = fixHalfSpaces(text)
        if (fixed !== text) node.nodeValue = fixed
        continue
      }

      if (node.nodeType !== Node.ELEMENT_NODE) continue

      const child = node as Element
      const tag = child.tagName.toUpperCase()

      if (DROPPED_WITH_CONTENT.has(tag)) {
        child.remove()
        continue
      }

      if (!ALLOWED_TAGS.has(tag)) {
        /*
         | تگِ ناشناخته (`<font>`، `<o:p>`ِ Word) باز می‌شود و فرزندانش —
         | پس از پاک‌سازی — سرِ جایش می‌نشینند.
         |
         | ⚠️ پیش‌تر به متنِ خالص تبدیل می‌شد و هر پررنگی یا شکستِ سطری
         | که داخلش بود از بین می‌رفت.
         */
        walk(child)
        child.replaceWith(...Array.from(child.childNodes))
        continue
      }

      for (const attr of Array.from(child.attributes)) {
        if (!ALLOWED_ATTRS.has(attr.name)) {
          child.removeAttribute(attr.name)
          continue
        }
        if (attr.name === 'style') {
          const clean = sanitizeStyle(attr.value)
          if (clean) child.setAttribute('style', clean)
          else child.removeAttribute('style')
        }
        if (attr.name === 'href' && /^\s*javascript:/i.test(attr.value)) {
          child.removeAttribute('href')
        }
      }

      walk(child)
    }
  }

  walk(root)
  return root.innerHTML
}

/* ------------------------------------------------------------------ */

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      dir = 'rtl',
      placeholder = 'متن نامه را بنویسید…',
      disabled = false,
      showRuler = true,
      letterhead,
      footer,
      className,
      minHeightMm = 180,
    },
    ref,
  ) {
    const editorRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLDivElement>(null)
    const [margins, setMargins] = useState(DEFAULT_MARGIN_MM)
    const [activeDir, setActiveDir] = useState<'rtl' | 'ltr'>(dir)

    /*
     | ------------------------------------------------------------------
     | 🔴 بزرگ‌نمایی دو حالت دارد: «اندازهٔ ظرف» و عددِ دستی.
     |
     | ⚠️ پیش‌فرض `fit` است — کاغذ A4 همیشه به عرضِ ظرفش درمی‌آید و
     | هیچ‌وقت از آن بیرون نمی‌زند. تا وقتی کاربر خودش عدد را دست
     | نزده، هر تغییرِ عرض دوباره اندازه را حساب می‌کند.
     | ------------------------------------------------------------------ */
    const [zoomMode, setZoomMode] = useState<'fit' | 'manual'>('fit')
    const [manualZoom, setManualZoom] = useState(1)
    const [fitZoom, setFitZoom] = useState(1)

    /*
     | 🔴 نسبتِ میلی‌متر به پیکسل را باید از مرورگر پرسید، نه از
     | ۹۶dpi حساب کرد.
     |
     | ⚠️ این نسبت با بزرگ‌نماییِ خودِ مرورگر (Ctrl و +) عوض می‌شود.
     | عددِ ثابت در آن حالت چند پیکسل خطا می‌دهد — و همان چند پیکسل
     | است که نوارِ افقی می‌آورد.
     */
    const pxPerMm = usePxPerMm()

    /*
     | ⚠️ عرضِ ظرف با `ResizeObserver` دنبال می‌شود نه با رویدادِ
     | `resize` پنجره: ویرایشگر داخلِ کارتی است که عرضش با بازوبستهٔ
     | ستونِ کناری هم عوض می‌شود، بی‌آنکه پنجره تکان بخورد.
     */
    const canvasWidth = useElementWidth(canvasRef)

    const zoom = zoomMode === 'fit' ? fitZoom : manualZoom

    useEffect(() => {
      if (!canvasWidth || !pxPerMm) return
      const pageWidthPx = A4_WIDTH_MM * pxPerMm
      /* کفِ ۰.۳۵ تا روی گوشی متن به‌کلی ناخوانا نشود. */
      const next = Math.min(1, Math.max(0.35, canvasWidth / pageWidthPx))
      setFitZoom(Number(next.toFixed(4)))
    }, [canvasWidth, pxPerMm])

    const setZoom = useCallback((z: number) => {
      setZoomMode('manual')
      setManualZoom(z)
    }, [])

    /*
     | 🔴 مقدار بیرونی فقط وقتی نوشته می‌شود که با محتوای فعلی فرق دارد.
     |
     | ⚠️ اگر هر بار `innerHTML` را ست کنیم، نشانگر در هر کلید به ابتدای
     | متن می‌پرد — رایج‌ترین باگ ویرایشگرهای دست‌ساز، و در فارسی
     | آزاردهنده‌تر چون کاربر متوجهِ جهش نمی‌شود تا وقتی جمله خراب شود.
     */
    useEffect(() => {
      const el = editorRef.current
      if (el && el.innerHTML !== value) el.innerHTML = value || ''
    }, [value])

    const emit = useCallback(() => {
      const el = editorRef.current
      if (el) onChange(el.innerHTML)
    }, [onChange])

    const exec = useCallback(
      (command: string, arg?: string) => {
        if (disabled) return
        editorRef.current?.focus()
        document.execCommand(command, false, arg)
        emit()
      },
      [disabled, emit],
    )

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        const html = e.clipboardData.getData('text/html')
        if (!html) return /* متن ساده مشکلی ندارد */
        e.preventDefault()
        document.execCommand('insertHTML', false, sanitizeLetterHtml(prepareClipboardHtml(html)))
        emit()
      },
      [emit],
    )

    const print = useCallback(() => {
      const el = editorRef.current
      if (!el) return
      printLetter(el.innerHTML, { margins, dir: activeDir, letterhead: null, footer: null })
    }, [margins, activeDir])

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      print,
      getHtml: () => editorRef.current?.innerHTML ?? '',
    }))

    const contentWidthMm = A4_WIDTH_MM - margins.left - margins.right

    /*
     | ------------------------------------------------------------------
     | 🔴 `transform: scale` عرضِ **چیدمان** را عوض نمی‌کند.
     |
     | ⚠️ اینجا بود که نوارِ افقی می‌آمد: کاغذ با `scale(0.6)` کوچک
     | دیده می‌شد ولی مرورگر همچنان ۲۱۰ میلی‌متر جا برایش می‌گرفت و
     | ظرف به همان اندازه پهن می‌ماند. پس اندازهٔ واقعیِ پس از مقیاس
     | را خودمان روی یک قابِ بیرونی می‌گذاریم و کاغذ داخلش مقیاس
     | می‌خورد — آن‌وقت چیدمان همان چیزی را می‌بیند که چشم می‌بیند.
     |
     | ⚠️ ارتفاع هم باید همین کار را بشود، وگرنه زیرِ کاغذِ کوچک‌شده
     | فضای خالیِ بی‌دلیل می‌ماند. با `ResizeObserver` روی خودِ کاغذ
     | ارتفاعِ واقعی‌اش را می‌گیریم و در مقیاس ضرب می‌کنیم.
     | ------------------------------------------------------------------ */
    const pageRef = useRef<HTMLDivElement>(null)
    const pageHeight = useElementHeight(pageRef)

    const frameStyle: React.CSSProperties = {
      width: `${A4_WIDTH_MM * zoom}mm`,
      height: pageHeight ? `${pageHeight * zoom}px` : undefined,
    }

    return (
      <div className={cn('rte', className)} data-disabled={disabled || undefined}>
        <RichTextToolbar
          exec={exec}
          disabled={disabled}
          activeDir={activeDir}
          onDirChange={(d) => {
            setActiveDir(d)
            exec(d === 'rtl' ? 'insertHTML' : 'insertHTML', '')
            /* جهت روی خودِ ناحیهٔ ویرایش اعمال می‌شود، نه روی انتخاب. */
          }}
          zoom={zoom}
          zoomMode={zoomMode}
          onZoom={setZoom}
          onFit={() => setZoomMode('fit')}
          onPrint={print}
        />

        {/*
          ⚠️ نوارِ افقی فقط وقتی باز می‌شود که بزرگ‌نماییِ دستی از
          «اندازهٔ صفحه» جلو زده باشد. کوچک‌کردنِ دستی همچنان چیزی
          برای لغزاندن ندارد، پس نواری هم لازم نیست.
        */}
        <div
          className="rte__canvas"
          ref={canvasRef}
          data-zoom={zoomMode === 'manual' && zoom > fitZoom ? 'manual' : undefined}
        >
          {showRuler && (
            /*
              ⚠️ ارتفاعِ قابِ خط‌کش هم مقیاس‌خورده است: خط‌کش ۱.۵rem
              بلند است، پس قاب باید همان‌قدر × مقیاس جا بگیرد وگرنه
              کاغذ رویش می‌افتد.
            */
            <div
              className="rte__scale"
              style={{ width: frameStyle.width, height: `calc(1.5rem * ${zoom})` }}
            >
              <Ruler
                widthMm={A4_WIDTH_MM}
                margins={margins}
                dir={activeDir}
                onMarginChange={setMargins}
                zoom={zoom}
                pxPerMm={pxPerMm}
              />
            </div>
          )}

          <div className="rte__scale" style={frameStyle}>
            <div
              ref={pageRef}
              className="rte__page"
              style={{
                width: `${A4_WIDTH_MM}mm`,
                transform: `scale(${zoom})`,
                transformOrigin: activeDir === 'rtl' ? 'top right' : 'top left',
              }}
            >
              {letterhead ? <div className="rte__letterhead">{letterhead}</div> : null}

              <div
                ref={editorRef}
                className="rte__content"
                contentEditable={!disabled}
                suppressContentEditableWarning
                dir={activeDir}
                data-placeholder={placeholder}
                onInput={emit}
                onBlur={emit}
                onPaste={handlePaste}
                style={{
                  paddingTop: `${margins.top}mm`,
                  paddingBottom: `${margins.bottom}mm`,
                  paddingRight: `${margins.right}mm`,
                  paddingLeft: `${margins.left}mm`,
                  minHeight: `${minHeightMm}mm`,
                  width: `${A4_WIDTH_MM}mm`,
                }}
              />

              {footer ? <div className="rte__footer">{footer}</div> : null}
            </div>
          </div>
        </div>

        <div className="rte__status">
          <span>عرض متن: {toPersian(contentWidthMm.toFixed(0))} میلی‌متر</span>
          {/* ⚠️ روی گوشی پنهان می‌شود: آنجا متن جاری است و مقیاسی در کار نیست. */}
          <span className="rte__status-zoom">
            بزرگ‌نمایی: {toPersian(String(Math.round(zoom * 100)))}٪
            {zoomMode === 'fit' ? ' (اندازهٔ صفحه)' : null}
          </span>
        </div>
      </div>
    )
  },
)

/* ==================================================================
   نمای سند — همان کاغذ، فقط خواندنی
   ==================================================================
   🔴 چرا اینجا و نه یک فایل تازه: قاعده‌های کاغذ (`rte__canvas`،
   `rte__scale`، `rte__page`، `rte__content`) و هر سه قلاب مقیاس
   (`usePxPerMm`، `useElementWidth`، `useElementHeight`) همین‌جا
   هستند. کپی‌کردنشان در فایلی دیگر یعنی دو کاغذ که به‌مرور از هم
   واگرا می‌شوند — و همان تله‌های حل‌شده (نوارِ افقی، `contain`،
   فهرستِ بی‌نشانه) دوباره برمی‌گردند.

   ⚠️ تفاوتش با ویرایشگر فقط سه چیز است: `contentEditable` ندارد،
   خط‌کش و نوارِ ابزار ندارد، و حاشیه‌ها ثابت‌اند (کسی که نامه را
   می‌خواند حق جابه‌جاکردنِ حاشیهٔ سند را ندارد).
   ================================================================== */

export interface LetterPaperProps {
  /** متنِ نامه — HTML. پیش از نمایش پاک‌سازی می‌شود. */
  html: string
  /** سرلوحه — شماره، تاریخ و موضوع. روی خودِ کاغذ می‌نشیند. */
  letterhead?: ReactNode
  footer?: ReactNode
  dir?: 'rtl' | 'ltr'
  className?: string
}

export function LetterPaper({
  html,
  letterhead,
  footer,
  dir = 'rtl',
  className,
}: LetterPaperProps) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const pageRef = useRef<HTMLDivElement>(null)

  const pxPerMm = usePxPerMm()
  const canvasWidth = useElementWidth(canvasRef)
  const pageHeight = useElementHeight(pageRef)

  /*
   | ⚠️ همان قاعدهٔ «اندازهٔ صفحه»ِ ویرایشگر، بی‌حالتِ دستی: خواننده
   | دکمهٔ بزرگ‌نمایی ندارد، پس کاغذ همیشه به عرضِ ظرفش درمی‌آید و
   | هیچ‌وقت نوارِ افقی نمی‌سازد.
   |
   | 🔴 کفِ ۰.۳۵ عمداً همان عددِ ویرایشگر است تا متن روی گوشی به‌کلی
   | ناخوانا نشود.
   */
  const zoom =
    canvasWidth && pxPerMm
      ? Number(Math.min(1, Math.max(0.35, canvasWidth / (A4_WIDTH_MM * pxPerMm))).toFixed(4))
      : 1

  /*
   | 🔴 پاک‌سازی پیش از نمایش — و نه فقط موقع چسباندن.
   |
   | ⚠️ `body` هنگام ذخیره در سرور پاک‌سازی نمی‌شود، پس هر کسی که
   | مستقیم به API بزند می‌تواند `<script>` در متنِ نامه بنشاند. این
   | نما همان متن را با `innerHTML` رندر می‌کند، یعنی بدون پاک‌سازی
   | تبدیل به راهِ اجرا می‌شد.
   */
  const safe = useMemo(() => sanitizeLetterHtml(html), [html])

  const frameStyle: React.CSSProperties = {
    width: `${A4_WIDTH_MM * zoom}mm`,
    height: pageHeight ? `${pageHeight * zoom}px` : undefined,
  }

  const m = DEFAULT_MARGIN_MM

  return (
    <div className={cn('rte', className)}>
      <div className="rte__canvas" ref={canvasRef}>
        <div className="rte__scale" style={frameStyle}>
          <div
            ref={pageRef}
            className="rte__page"
            style={{
              width: `${A4_WIDTH_MM}mm`,
              transform: `scale(${zoom})`,
              transformOrigin: dir === 'rtl' ? 'top right' : 'top left',
            }}
          >
            {/*
              ⚠️ سرلوحه داخلِ حاشیهٔ کاغذ می‌نشیند و نه در `rte__letterhead`:
              آن کلاس برای یادداشتِ بالای ویرایشگر است (خاکستری و ریز)، ولی
              شمارهٔ نامه بخشی از خودِ سند است و باید در همان تراز و همان
              حاشیه‌ای باشد که روی کاغذ چاپ می‌شود.
            */}
            <div
              className="rte__content"
              dir={dir}
              style={{
                paddingTop: `${m.top}mm`,
                paddingBottom: `${m.bottom}mm`,
                paddingRight: `${m.right}mm`,
                paddingLeft: `${m.left}mm`,
                minHeight: '297mm',
                width: `${A4_WIDTH_MM}mm`,
              }}
            >
              {letterhead}
              <div dangerouslySetInnerHTML={{ __html: safe }} />
              {footer}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------
   نوار ابزار
   ------------------------------------------------------------------ */

type ToolbarProps = {
  exec: (command: string, arg?: string) => void
  disabled: boolean
  activeDir: 'rtl' | 'ltr'
  onDirChange: (dir: 'rtl' | 'ltr') => void
  zoom: number
  zoomMode: 'fit' | 'manual'
  onZoom: (z: number) => void
  onFit: () => void
  onPrint: () => void
}

function RichTextToolbar({
  exec,
  disabled,
  activeDir,
  onDirChange,
  zoom,
  zoomMode,
  onZoom,
  onFit,
  onPrint,
}: ToolbarProps) {
  const group = 'rte__group'

  return (
    <div className="rte__toolbar" role="toolbar" aria-label="ابزار نگارش نامه">
      <div className={group}>
        <ToolButton title="واگرد (Ctrl+Z)" onClick={() => exec('undo')} disabled={disabled}>↶</ToolButton>
        <ToolButton title="ازنو (Ctrl+Y)" onClick={() => exec('redo')} disabled={disabled}>↷</ToolButton>
      </div>

      <div className={group}>
        <select
          className="rte__select"
          title="قلم"
          disabled={disabled}
          onChange={(e) => exec('fontName', e.target.value)}
          defaultValue={FONTS[0].value}
        >
          {FONTS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        {/*
          ⚠️ `fontSize` در execCommand فقط ۱..۷ می‌پذیرد، نه پیکسل.
          پس اندازهٔ واقعی را با یک span و style اعمال می‌کنیم.
        */}
        <select
          className="rte__select rte__select--sm"
          title="اندازهٔ قلم"
          disabled={disabled}
          onChange={(e) => applyFontSize(exec, Number(e.target.value))}
          defaultValue={12}
        >
          {SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className={group}>
        <ToolButton title="درشت (Ctrl+B)" onClick={() => exec('bold')} disabled={disabled}><b>ب</b></ToolButton>
        <ToolButton title="کج (Ctrl+I)" onClick={() => exec('italic')} disabled={disabled}><i>ک</i></ToolButton>
        <ToolButton title="زیرخط (Ctrl+U)" onClick={() => exec('underline')} disabled={disabled}><u>ز</u></ToolButton>
        <ToolButton title="خط‌خورده" onClick={() => exec('strikeThrough')} disabled={disabled}><s>خ</s></ToolButton>
        <ToolButton title="بالانویس" onClick={() => exec('superscript')} disabled={disabled}>x²</ToolButton>
        <ToolButton title="پایین‌نویس" onClick={() => exec('subscript')} disabled={disabled}>x₂</ToolButton>
      </div>

      <div className={group}>
        <ColorPicker title="رنگ متن" colors={COLORS} onPick={(c) => exec('foreColor', c)} disabled={disabled}>
          A
        </ColorPicker>
        <ColorPicker
          title="رنگ زمینه"
          colors={HIGHLIGHTS}
          onPick={(c) => exec('hiliteColor', c)}
          disabled={disabled}
        >
          ▨
        </ColorPicker>
      </div>

      <div className={group}>
        {/*
          🔴 ترتیب دکمه‌ها در حالت راست‌به‌چپ برعکس است.

          ⚠️ در نامهٔ فارسی «تراز راست» پیش‌فرض است و باید اولین دکمه
          باشد. اگر ترتیب LTR را کپی می‌کردیم، کاربر هر بار دنبال
          دکمه‌ای می‌گشت که همیشه سرِ جای غلط است.
        */}
        <ToolButton title="تراز راست" onClick={() => exec('justifyRight')} disabled={disabled}>⯈</ToolButton>
        <ToolButton title="وسط‌چین" onClick={() => exec('justifyCenter')} disabled={disabled}>≡</ToolButton>
        <ToolButton title="تراز چپ" onClick={() => exec('justifyLeft')} disabled={disabled}>⯇</ToolButton>
        <ToolButton title="هم‌ترازی دوطرفه" onClick={() => exec('justifyFull')} disabled={disabled}>☰</ToolButton>
      </div>

      <div className={group}>
        <ToolButton title="فهرست نشانه‌دار" onClick={() => exec('insertUnorderedList')} disabled={disabled}>•</ToolButton>
        <ToolButton title="فهرست شماره‌دار" onClick={() => exec('insertOrderedList')} disabled={disabled}>۱.</ToolButton>
        <ToolButton title="افزایش تورفتگی" onClick={() => exec('indent')} disabled={disabled}>⇥</ToolButton>
        <ToolButton title="کاهش تورفتگی" onClick={() => exec('outdent')} disabled={disabled}>⇤</ToolButton>
      </div>

      <div className={group}>
        <select
          className="rte__select"
          title="سبک پاراگراف"
          disabled={disabled}
          onChange={(e) => exec('formatBlock', e.target.value)}
          defaultValue="p"
        >
          <option value="p">متن عادی</option>
          <option value="h1">عنوان ۱</option>
          <option value="h2">عنوان ۲</option>
          <option value="h3">عنوان ۳</option>
          <option value="blockquote">نقل‌قول</option>
        </select>
      </div>

      <div className={group}>
        <ToolButton title="درج جدول ۳×۳" onClick={() => insertTable(exec, 3, 3)} disabled={disabled}>▦</ToolButton>
        <ToolButton title="خط جداکننده" onClick={() => exec('insertHorizontalRule')} disabled={disabled}>―</ToolButton>
        <ToolButton title="پاک‌کردن قالب" onClick={() => exec('removeFormat')} disabled={disabled}>⌫</ToolButton>
      </div>

      <div className={group}>
        <ToolButton
          title="راست‌به‌چپ"
          onClick={() => onDirChange('rtl')}
          active={activeDir === 'rtl'}
          disabled={disabled}
        >
          ←ا
        </ToolButton>
        <ToolButton
          title="چپ‌به‌راست"
          onClick={() => onDirChange('ltr')}
          active={activeDir === 'ltr'}
          disabled={disabled}
        >
          ا→
        </ToolButton>
      </div>

      <div className={cn(group, 'rte__group--end')}>
        <ToolButton title="کوچک‌نمایی" onClick={() => onZoom(Math.max(0.35, round2(zoom - 0.1)))}>−</ToolButton>
        <span className="rte__zoom">{toPersian(String(Math.round(zoom * 100)))}٪</span>
        <ToolButton title="بزرگ‌نمایی" onClick={() => onZoom(Math.min(2, round2(zoom + 0.1)))}>+</ToolButton>
        {/*
          ⚠️ راهِ بازگشت به «اندازهٔ صفحه».

          🔴 بدون این دکمه، کاربری که یک‌بار روی `+` زده تا آخرِ کار
          با نوارِ افقی می‌ماند و راهی ندارد جز بارگذاری دوبارهٔ صفحه.
        */}
        <ToolButton
          title="اندازهٔ صفحه"
          onClick={onFit}
          active={zoomMode === 'fit'}
        >
          ⤢
        </ToolButton>
        <ToolButton title="چاپ نامه" onClick={onPrint}>🖨</ToolButton>
      </div>
    </div>
  )
}

function ToolButton({
  children,
  title,
  onClick,
  disabled,
  active,
}: {
  children: ReactNode
  title: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
}) {
  return (
    <button
      type="button"
      className={cn('rte__btn', active && 'rte__btn--active')}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      /*
       | 🔴 `onMouseDown` با preventDefault — نه `onClick` تنها.
       |
       | ⚠️ بدون آن، کلیک روی دکمه ابتدا فوکوس را از ناحیهٔ ویرایش
       | می‌گیرد و انتخاب کاربر از بین می‌رود؛ آن‌وقت «درشت‌کردن»
       | روی هیچ‌چیز اعمال می‌شود و کاربر فکر می‌کند دکمه خراب است.
       */
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function ColorPicker({
  children,
  title,
  colors,
  onPick,
  disabled,
}: {
  children: ReactNode
  title: string
  colors: string[]
  onPick: (color: string) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)

  return (
    <span className="rte__color">
      <ToolButton title={title} onClick={() => setOpen((o) => !o)} disabled={disabled}>
        {children}
      </ToolButton>
      {open && (
        <span className="rte__swatches" role="listbox" aria-label={title}>
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              className="rte__swatch"
              style={{ background: c }}
              title={c}
              aria-label={c}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPick(c)
                setOpen(false)
              }}
            />
          ))}
        </span>
      )}
    </span>
  )
}

/* ------------------------------------------------------------------
   خط‌کش — با دستگیرهٔ حاشیه، مثل ورد
   ------------------------------------------------------------------ */

function Ruler({
  widthMm,
  margins,
  dir,
  onMarginChange,
  zoom,
  pxPerMm,
}: {
  widthMm: number
  margins: typeof DEFAULT_MARGIN_MM
  dir: 'rtl' | 'ltr'
  onMarginChange: (m: typeof DEFAULT_MARGIN_MM) => void
  zoom: number
  pxPerMm: number
}) {
  /*
   | 🔴 واحد خط‌کش سانتی‌متر است، نه اینچ.
   |
   | ⚠️ ورد فارسی هم سانتی‌متر نشان می‌دهد. اینچ برای کاربر ایرانی
   | هیچ معنایی ندارد و «حاشیهٔ ۱ اینچی» قابل تصور نیست.
   */
  const ticks = useMemo(() => {
    const out: { cm: number; major: boolean }[] = []
    for (let mm = 0; mm <= widthMm; mm += 5) {
      out.push({ cm: mm / 10, major: mm % 10 === 0 })
    }
    return out
  }, [widthMm])

  const startKey = dir === 'rtl' ? 'right' : 'left'
  const endKey = dir === 'rtl' ? 'left' : 'right'

  const drag = (which: 'start' | 'end') => (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const key = which === 'start' ? startKey : endKey
    const initial = margins[key as keyof typeof margins]

    const move = (ev: MouseEvent) => {
      /*
       | px → mm، با احتساب بزرگ‌نمایی.
       |
       | ⚠️ نسبتِ اندازه‌گیری‌شده و نه ثابتِ ۹۶dpi: حالا که کاغذ
       | خودش را با عرضِ ظرف جور می‌کند، بزرگ‌نمایی عددهای کسری
       | می‌گیرد و خطای آن ثابت زیرِ انگشتِ کاربر دیده می‌شود —
       | دستگیره از نشانگر عقب می‌ماند.
       */
      const deltaPx = (ev.clientX - startX) / zoom
      const deltaMm = deltaPx / pxPerMm
      const signed = dir === 'rtl' && which === 'start' ? -deltaMm : deltaMm
      const next = Math.max(5, Math.min(80, initial + signed))
      onMarginChange({ ...margins, [key]: Math.round(next) })
    }

    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }

    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  return (
    <div
      className="rte__ruler"
      style={{
        width: `${widthMm}mm`,
        transform: `scale(${zoom})`,
        /* ⚠️ هم‌مبدأ با کاغذ، وگرنه خط‌کش و متن روی هم نمی‌افتند. */
        transformOrigin: dir === 'rtl' ? 'top right' : 'top left',
      }}
      dir={dir}
    >
      <div
        className="rte__ruler-margin"
        style={{ [startKey]: 0, width: `${margins[startKey as keyof typeof margins]}mm` } as React.CSSProperties}
      />
      <div
        className="rte__ruler-margin"
        style={{ [endKey]: 0, width: `${margins[endKey as keyof typeof margins]}mm` } as React.CSSProperties}
      />

      {ticks.map((t) => (
        <span
          key={t.cm}
          className={cn('rte__tick', t.major && 'rte__tick--major')}
          style={{ [startKey]: `${t.cm * 10}mm` } as React.CSSProperties}
        >
          {t.major && t.cm > 0 ? <em>{toPersian(String(t.cm))}</em> : null}
        </span>
      ))}

      <button
        type="button"
        className="rte__handle"
        title="حاشیهٔ ابتدای متن"
        aria-label="حاشیهٔ ابتدای متن"
        style={{ [startKey]: `${margins[startKey as keyof typeof margins]}mm` } as React.CSSProperties}
        onMouseDown={drag('start')}
      />
      <button
        type="button"
        className="rte__handle"
        title="حاشیهٔ انتهای متن"
        aria-label="حاشیهٔ انتهای متن"
        style={{ [endKey]: `${margins[endKey as keyof typeof margins]}mm` } as React.CSSProperties}
        onMouseDown={drag('end')}
      />
    </div>
  )
}

/* ------------------------------------------------------------------
   کمکی‌ها
   ------------------------------------------------------------------ */

function toPersian(v: string): string {
  return v.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
}

/** ⚠️ بدون گِردکردن، جمعِ اعشاریِ ۰.۱ عددهایی مثل ۸۹.۹۹۹٪ می‌سازد. */
function round2(v: number): number {
  return Math.round(v * 100) / 100
}

/**
 * پهنای واقعیِ یک میلی‌متر بر حسب پیکسل.
 *
 * ⚠️ با یک عنصرِ آزمایشیِ ۱۰۰ میلی‌متری اندازه گرفته می‌شود، نه با
 * ثابتِ ۹۶/۲۵.۴: آن ثابت با بزرگ‌نماییِ مرورگر یا چگالیِ نمایشگر
 * جور درنمی‌آید و خطایش دقیقاً همان‌جایی ظاهر می‌شود که نباید —
 * لبهٔ کاغذ.
 */
function usePxPerMm(): number {
  const [ratio, setRatio] = useState(96 / 25.4)

  useEffect(() => {
    const probe = document.createElement('div')
    probe.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;width:100mm;height:0;'
    document.body.appendChild(probe)
    const measured = probe.getBoundingClientRect().width / 100
    probe.remove()
    if (measured > 0) setRatio(measured)
  }, [])

  return ratio
}

/**
 * عرضِ درونیِ یک عنصر (بدون padding) — دنبال‌شده با `ResizeObserver`.
 *
 * 🔴 `contentBoxSize` و نه `offsetWidth`: کاغذ باید داخلِ **ناحیهٔ
 * محتوا** جا شود؛ اگر padding ظرف را حساب نکنیم، کاغذ همیشه به
 * اندازهٔ همان padding بیرون می‌زند.
 */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = Array.isArray(entry.contentBoxSize)
          ? entry.contentBoxSize[0]
          : (entry.contentBoxSize as unknown as ResizeObserverSize | undefined)
        const next = box ? box.inlineSize : entry.contentRect.width
        /* ⚠️ تغییرِ زیرِ یک پیکسل نادیده — وگرنه رندر و ناظر حلقه می‌شوند. */
        setWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next))
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])

  return width
}

/**
 * ارتفاعِ واقعیِ عنصرِ مقیاس‌خورده — پیش از اعمالِ `scale`.
 *
 * ⚠️ `offsetHeight` به‌کار می‌رود و نه `getBoundingClientRect`:
 * دومی ارتفاعِ **پس از** مقیاس را می‌دهد و اگر آن را دوباره در مقیاس
 * ضرب کنیم، هر رندر کوچک‌تر از قبل می‌شود و قاب تا صفر جمع می‌شود.
 */
function useElementHeight(ref: React.RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return

    /*
     | ⚠️ `setHeight` فقط وقتی صدا زده می‌شود که عدد واقعاً عوض شده
     | باشد. `offsetHeight` عددِ صحیح می‌دهد، ولی ناظر با هر تغییرِ
     | کسریِ اندازه هم شلیک می‌کند؛ بی این شرط، رندر ← ناظر ← رندر
     | حلقه می‌شود.
     */
    const read = () => setHeight((prev) => (prev === el.offsetHeight ? prev : el.offsetHeight))
    read()

    const observer = new ResizeObserver(read)
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref])

  return height
}

/**
 * اندازهٔ قلم واقعی.
 *
 * ⚠️ `execCommand('fontSize')` فقط ۱..۷ می‌پذیرد که به `<font size>`
 * ترجمه می‌شود — تگی که در چاپ رفتار قابل پیش‌بینی ندارد. پس با همان
 * دستور یک نشانه می‌گذاریم و بلافاصله به `style` تبدیلش می‌کنیم.
 */
function applyFontSize(exec: (c: string, a?: string) => void, pt: number) {
  exec('fontSize', '7')
  const marks = document.querySelectorAll('font[size="7"]')
  marks.forEach((m) => {
    const span = document.createElement('span')
    span.style.fontSize = `${pt}pt`
    span.innerHTML = m.innerHTML
    m.replaceWith(span)
  })
}

function insertTable(exec: (c: string, a?: string) => void, rows: number, cols: number) {
  const cell = '<td style="border: 1px solid #94a3b8; padding: 4px 8px; min-width: 60px;">&nbsp;</td>'
  const row = `<tr>${cell.repeat(cols)}</tr>`
  const table =
    `<table style="border-collapse: collapse; width: 100%;">${row.repeat(rows)}</table><p><br></p>`
  exec('insertHTML', table)
}

/* ------------------------------------------------------------------
   چاپ — راست‌به‌چپ، A4، با حاشیهٔ واقعی
   ------------------------------------------------------------------
   🔴 چرا پنجرهٔ جدا و نه `window.print()` روی همین صفحه.

   چاپ مستقیم یعنی همهٔ CSS برنامه (منو، نوار کناری، Tailwind reset)
   وارد صفحهٔ چاپ می‌شود. حتی با `@media print` و کلی `display:none`،
   یک قاعدهٔ فراموش‌شده کافی است تا نامه با حاشیهٔ غلط چاپ شود — و این
   روی کاغذِ رسمی دیده می‌شود، نه روی صفحه.

   ⚠️ سند مستقل یعنی خروجی چاپ دقیقاً همان چیزی است که اینجا نوشته
   شده و هیچ چیز دیگری.
   ------------------------------------------------------------------ */

export function printLetter(
  html: string,
  options: {
    margins?: typeof DEFAULT_MARGIN_MM
    dir?: 'rtl' | 'ltr'
    letterhead?: string | null
    footer?: string | null
    title?: string
  } = {},
): void {
  const m = options.margins ?? DEFAULT_MARGIN_MM
  const dir = options.dir ?? 'rtl'
  const title = options.title ?? 'نامه'

  const win = window.open('', '_blank', 'width=900,height=1000')
  if (!win) return

  win.document.write(`<!doctype html>
<html lang="fa" dir="${dir}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page {
    size: A4;
    margin: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;
  }
  html, body {
    margin: 0;
    padding: 0;
    direction: ${dir};
    font-family: IRANSans, Vazirmatn, Tahoma, sans-serif;
    font-size: 12pt;
    line-height: 1.9;
    color: #000;
  }
  /*
    ⚠️ فهرست شماره‌دار باید ارقام فارسی بدهد.
    بدون این، نامهٔ فارسی با «1. 2. 3.» چاپ می‌شود.
  */
  ol { list-style-type: persian; }
  ul, ol { padding-inline-start: 22px; }
  table { border-collapse: collapse; width: 100%; }
  td, th { border: 1px solid #333; padding: 4px 8px; }
  blockquote {
    margin-inline-start: 0;
    padding-inline-start: 12px;
    border-inline-start: 3px solid #94a3b8;
    color: #334155;
  }
  .letterhead { margin-bottom: 10mm; }
  .footer { margin-top: 14mm; }
  /*
    🔴 جلوگیری از شکستنِ جدول و امضا بین دو صفحه.
    امضایی که تنها در صفحهٔ بعد چاپ شود، سند را بی‌اعتبار می‌کند.
  */
  tr, td, th { break-inside: avoid; }
  .footer { break-inside: avoid; }
</style>
</head>
<body>
  ${options.letterhead ? `<div class="letterhead">${options.letterhead}</div>` : ''}
  <div class="body">${html}</div>
  ${options.footer ? `<div class="footer">${options.footer}</div>` : ''}
</body>
</html>`)

  win.document.close()

  /*
   | ⚠️ صبر برای بارگذاری قلم‌ها.
   |
   | بدون آن، چاپ با قلم جایگزین انجام می‌شود و فاصلهٔ حروف فارسی
   | به هم می‌ریزد — روی صفحه درست دیده می‌شود، روی کاغذ نه.
   */
  win.onload = () => {
    win.focus()
    win.print()
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )
}
